'use client';

import Autoplay from 'embla-carousel-autoplay';
import useEmblaCarousel from 'embla-carousel-react';
import Image from 'next/image';
import { useLocale, useTranslations } from 'next-intl';
import { memo, useCallback, useEffect, useMemo, useRef } from 'react';
import Quote from '@/app/assets/imgs/quote.png';
import { BaseLineQuestion } from '@/app/components/icons/BaseLineQuestion';
import { ChannelX } from '@/app/components/icons/ChannelX';
import { SITE } from '@/app/constants';
import { useDevice } from '@/app/hooks';
import { cn, reportEvent } from '@/app/utils';
import { openOrRedirect } from '@/app/utils/client';
import { ChannelSubscribe } from './ChannelSubscribe';
import { CREATORS_CONFIG } from './constants';
import type { Creator } from './type';

// 为实现无限滚动，复制足够的数据；摊平成单个 creator，单行展示
const getCreatorData = (count = 3) => {
  const flat = CREATORS_CONFIG.flat();
  const result: Creator[] = [];
  for (let i = 0; i < count; i++) {
    result.push(...flat);
  }
  return result;
};

// 记忆化引用类型，避免不必要的重新计算
const autoplayOptions = {
  delay: 500, // 移动设备慢速滚动，桌面端极慢滚动
  playOnInit: false, // 初始不自动开始滚动，等待进入视野
  stopOnInteraction: false, // 交互时不停止滚动
  stopOnMouseEnter: true, // 鼠标悬停时暂停
  rootNode: (emblaRoot: HTMLElement) => emblaRoot.parentElement,
};

// 轮播配置
const carouselOptions = {
  loop: true, // 无限循环
  dragFree: true, // 自由拖动
  containScroll: 'trimSnaps' as const, // 明确类型为常量
  align: 'start' as const, // 明确类型为常量
  slidesToScroll: 1,
  duration: 60000, // 控制动画持续时间（值越大动画越慢）
};

// 使用memo包装CreatorCard组件以避免不必要的重渲染
const CreatorCard = memo(({ creator, onClick }: { creator: Creator; onClick: () => void }) => (
  <div className="w-[400px] shrink-0 rounded-12 border-[1px] border-system-white08 bg-[linear-gradient(180deg,rgba(153,153,153,0.08)0%,rgba(255,255,255,0.08)100%)] p-6 md:w-[280px] md:p-4">
    <div className="flex justify-between">
      <Image
        src={Quote.src}
        alt="quote"
        className="h-[32px] py-1 md:h-[22px] md:py-0.5"
        width={32}
        height={32}
        loading="lazy"
        decoding="async"
      />
      {creator?.xAccount ? (
        <ChannelX className="size-8 rounded-8 bg-system-white12 p-1 md:size-[22px] md:p-0.5" onClick={onClick} />
      ) : (
        <div />
      )}
    </div>
    <div className="mt-4 line-clamp-4 text-[14px] leading-[20px] md:mt-[11px] md:text-[12px] md:leading-[14px]">
      {creator.quote}
    </div>
    <div className="my-6 border-white border-b-[1px] opacity-10 md:my-4" />
    <div className="flex flex-row-reverse items-center gap-1">
      {SITE === 'global' && creator.order && (
        <>
          <div className="text-[12px] leading-[20px] md:text-[8px] md:leading-[12px]">{creator.order}</div>
          <ChannelSubscribe className="h-[18px] w-[18px] md:h-[12.5px] md:w-[12.5px]" />
        </>
      )}
      <div className="text-[14px] leading-[20px] md:text-[10px] md:leading-[14px]">{creator.name}</div>
      <Image
        src={creator.image}
        alt={creator.name}
        className="size-6 rounded-full object-cover md:size-4"
        width={24}
        height={24}
      />
    </div>
  </div>
));

CreatorCard.displayName = 'CreatorCard';

// 记忆化单个轮播项组件（单行展示，每个 slide 一张卡）
const CarouselSlide = memo(({ creator, index }: { creator: Creator; index: number }) => {
  // 使用useCallback包装点击处理函数，避免不必要的函数创建
  const handleCreatorClick = useCallback(() => {
    if (!creator?.xAccount) return;
    openOrRedirect(creator.xAccount);
    reportEvent('element_click', {
      element_name: 'X',
    });
  }, [creator]);

  return (
    <div key={`embla-slide-${creator.id}-${index}`} className="embla__slide px-1">
      <CreatorCard creator={creator} onClick={handleCreatorClick} />
    </div>
  );
});

CarouselSlide.displayName = 'CarouselSlide';

const Creators = () => {
  const { isMobile } = useDevice();
  const t = useTranslations('home3.creators');
  const locale = useLocale();
  const sectionRef = useRef<HTMLDivElement>(null);

  // 使用useMemo避免每次渲染时重新创建数据数组
  const creatorData = useMemo(() => getCreatorData(), []);

  // 使用useRef避免重复创建Autoplay实例
  const autoplayRef = useRef(Autoplay(autoplayOptions));

  // 轮播配置
  const [emblaRef, emblaApi] = useEmblaCarousel(carouselOptions, [autoplayRef.current]);

  // 监听组件可见性并控制轮播
  useEffect(() => {
    if (!sectionRef.current || !emblaApi) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const isIntersecting = entries[0].isIntersecting;

        const autoplay = emblaApi.plugins().autoplay;
        if (!autoplay) return;

        // 只有在桌面设备且可见时才启动自动播放
        if (isIntersecting && !isMobile) {
          autoplay.play();
        } else {
          autoplay.stop();
        }
      },
      { threshold: 0.1 }, // 当10%的内容可见时触发
    );

    observer.observe(sectionRef.current);

    // 组件卸载时清理
    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
      // 停止自动播放
      const autoplay = emblaApi.plugins().autoplay;
      if (autoplay) autoplay.stop();
    };
  }, [emblaApi, isMobile]);

  // 记忆化标题部分的渲染
  const renderHeader = useMemo(
    () => (
      <>
        <div className="mt-[160px] mb-6 flex h-[36px] w-[64px] items-center justify-center rounded-full bg-system-white24 md:hidden">
          <BaseLineQuestion className="size-6 text-system-white" />
        </div>
        <h2
          className={cn(
            'font-medium text-[24px] leading-normal md:mt-[97px] md:text-[30px]',
            locale === 'zh' && 'text-[36px]',
          )}
        >
          {t('title')}
        </h2>
        <div className="mb-[66px] text-center font-semibold text-[44px] leading-none md:text-[36px]">
          {locale !== 'zh' && t('subTitle')}
        </div>
      </>
    ),
    [locale, t],
  );

  // 记忆化轮播内容部分
  const renderCarousel = useMemo(
    () => (
      <div className="w-full overflow-hidden">
        <div className="embla" ref={emblaRef}>
          <div className="embla__container flex gap-2 py-2">
            {creatorData.map((creator, index) => (
              <CarouselSlide key={`slide-${creator.id}-${index}`} creator={creator} index={index} />
            ))}
          </div>
        </div>
      </div>
    ),
    [creatorData, emblaRef],
  );

  return (
    <div className="relative flex w-full flex-col items-center overflow-hidden" ref={sectionRef}>
      {renderHeader}
      {renderCarousel}
    </div>
  );
};

export default Creators;
